import { t } from '@lingui/core/macro';
import type { CarFleetViewRecord } from '@navio/nsdk';
import { EndOwnershipReason as EndOwnershipReasonValues, ServiceDepartmentModule } from '@navio/nsdk';
import { keepPreviousData } from '@tanstack/react-query';
import { RowSelectionState } from '@tanstack/react-table';
import { invalidateQuery, noddiAsync, URLKeys } from 'noddi-async';
import { brands } from 'noddi-types';
import {
  ApiErrorMessageInternal,
  ApiErrorPage,
  copyToClipboardWithToast,
  NoddiAsyncConfirmationDialog,
  NoddiLink,
  NoddiTable,
  RowSelectionAction,
  useFilterUnits,
  useNoddiSortOrderParams,
  usePaginationQueryParam
} from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import {
  CarContactSheet,
  fleetViewFilterUnit,
  getFleetViewColumns,
  useAuthContext,
  useBookingActions,
  useFleetViewTableFilters,
  useIsGroupAdmin,
  useNotificationOverviewCars
} from 'noddi-web';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAvailableBrandModules } from '../../../hooks/useAvailableBrandModules';
import { useBrandCapabilities } from '../../../hooks/useBrandCapabilities';
import { useCurrentBrand } from '../../../stores/BrandStore/brandStore';
import { getCommonTranslations, getTableTranslations } from '../../../utils/commonTranslations';
import { AddFleetCarSheet } from './AddFleetCarSheet';
import { type EndOwnershipAction, getEndOwnershipConfirmText, getEndOwnershipTitle } from './fleetEndOwnership';
import { FleetViewSummaryCards } from './FleetViewSummaryCards';
import { SwapFleetCarDialog, SwapTarget } from './SwapFleetCarDialog';

export const FleetView = () => {
  const commonTranslations = getCommonTranslations();
  const columnTranslations = commonTranslations.fleetView.columnTranslations;
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [driverCar, setDriverCar] = useState<CarFleetViewRecord>();
  const [endOwnershipAction, setEndOwnershipAction] = useState<EndOwnershipAction>();
  const [swapTarget, setSwapTarget] = useState<SwapTarget | null>(null);
  const navigate = useNavigate();
  const { clearStore, addCar } = useBookingActions();
  const { currentBrand } = useCurrentBrand();
  const { canCreateBooking } = useBrandCapabilities();
  const { availableModules } = useAvailableBrandModules();
  const hasFleetModule = availableModules.has(ServiceDepartmentModule.FLEET_SERVICES);

  const { pagination, setPagination } = usePaginationQueryParam();

  const { currentUserGroupId, userData } = useAuthContext();
  // Choosing who drives a car is the group admin's job; until the role is known the action stays off.
  const {
    isGroupAdmin,
    isLoading: isGroupAdminLoading,
    error: isGroupAdminError
  } = useIsGroupAdmin(currentUserGroupId ?? undefined, userData?.user.id);
  const canAssignDrivers = isGroupAdmin === true;
  // A swap cancels the outgoing car's upcoming bookings, so it is the group admin's call too.
  const canSwapCars = hasFleetModule && canAssignDrivers;

  const isCarGlass = currentBrand === brands.carglass;
  const showTireHotel = !isCarGlass;

  const baseColumns = getFleetViewColumns(columnTranslations, {
    showTireHotelColumns: showTireHotel,
    showDriverColumn: canAssignDrivers,
    showFleetScheduleColumn: hasFleetModule
  });
  const { order, onOrderChange } = useNoddiSortOrderParams({ columns: baseColumns });

  const filterTranslations = commonTranslations.fleetView.filterTranslations;
  const { filters: fleetViewFilters } = useFleetViewTableFilters(filterTranslations, {
    showTireHotelFilters: showTireHotel
  });
  const filters = useFilterUnits([
    fleetViewFilterUnit.locked({ userGroupIds: [currentUserGroupId!], brandDomains: [currentBrand] }),
    fleetViewFilters
  ]);

  const {
    data: fleetViewCarsResponse,
    isPending,
    isFetching,
    error: fleetViewCarsError
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedFleetViewCars,
    input: {
      ...pagination,
      order,
      ...filters.apiInput
    },
    queryConfig: {
      placeholderData: keepPreviousData
    }
  });

  const { data: notificationCars, error: notificationCarsError } = useNotificationOverviewCars(
    currentUserGroupId!,
    fleetViewCarsResponse?.results.map((car) => car.id) ?? [],
    canAssignDrivers
  );
  const columns = getFleetViewColumns(columnTranslations, {
    showTireHotelColumns: showTireHotel,
    showDriverColumn: canAssignDrivers,
    showFleetScheduleColumn: hasFleetModule,
    carManagers: notificationCars?.results
  });

  const {
    mutateAsync: endCarOwnership,
    isPending: isEndCarOwnershipPending,
    error: endCarOwnershipError
  } = noddiAsync.usePatch({
    type: URLKeys.patchRemoveCarsFromUserGroup,
    queryConfig: {
      onSuccess: async () => {
        invalidateQuery({ urlKey: URLKeys.getPaginatedFleetViewCars });
        invalidateQuery({ urlKey: URLKeys.getPaginatedNotificationOverviewCars });
        setEndOwnershipAction(undefined);
      }
    }
  });

  const selectedCars = fleetViewCarsResponse?.results.filter((car) => rowSelection[car.id]) ?? [];

  const prePopulateAndNavigate = () => {
    clearStore();

    selectedCars.forEach((car) => {
      const make = car.make;
      const model = car.model;

      addCar({
        make,
        model,
        hasTireHotel: car.wheelStorage.length > 0,
        image: car.image,
        makeLogo: car.makeLogo,
        displayName: car.displayName,
        licensePlate: car.licensePlate
      });
    });

    navigate(customerRoutes.newBookingAddress.getPath());
  };

  if (fleetViewCarsError) {
    return <ApiErrorPage error={fleetViewCarsError} />;
  }

  const singleSelectedSwapTarget = selectedCars.length === 1 ? toSwapTarget(selectedCars[0]) : null;
  const rowSelectionActions: RowSelectionAction[] = [
    ...(canCreateBooking ? [{ label: t`Create booking`, onClick: prePopulateAndNavigate }] : []),
    ...(canSwapCars
      ? [
          {
            label: t`Swap car`,
            onClick: () => setSwapTarget(singleSelectedSwapTarget),
            // Swapping replaces one car on its schedule, so it needs exactly one enrolled car.
            disabled: singleSelectedSwapTarget === null
          }
        ]
      : [])
  ];
  const canSelectRows = rowSelectionActions.length > 0;

  const driverCarGroup = notificationCars?.results.find((car) => car.carId === driverCar?.id);
  const endOwnershipLicensePlate = endOwnershipAction?.car.licensePlate.number;

  return (
    <div className='flex flex-col gap-5'>
      <div className='flex justify-end'>
        <NoddiLink to={customerRoutes.fleetCalendar.getPath()} size='small' startIcon='Calendar'>
          {t`Open booking calendar`}
        </NoddiLink>
      </div>
      {/* Driver assignment is an admin extra: a failed lookup leaves it off instead of hiding the fleet. */}
      {(isGroupAdminError || notificationCarsError) && (
        <ApiErrorMessageInternal error={[isGroupAdminError, notificationCarsError]} />
      )}
      {!isCarGlass && <FleetViewSummaryCards filterParams={filters.apiInput} />}
      <NoddiTable
        title={t`All cars`}
        headerNode={
          currentUserGroupId !== undefined ? <AddFleetCarSheet userGroupId={currentUserGroupId} /> : undefined
        }
        filters={filters}
        description={
          canCreateBooking
            ? t`List of all cars registered on your account. Select cars to create a new booking.`
            : t`List of all cars registered on your account.`
        }
        data={fleetViewCarsResponse?.results}
        columns={columns}
        getUrlPath={(item) => ({ url: customerRoutes.myCarDetails.getPath({ carId: item.id }) })}
        isPending={isPending || isGroupAdminLoading}
        isFetching={isFetching}
        pagination={pagination}
        setPagination={setPagination}
        totalCount={fleetViewCarsResponse?.count}
        getRowId={(row) => row.id}
        searchPlaceholder={t`Search by license plate, make or model`}
        // Selecting cars exists only to act on them: start a booking or swap one on its schedule.
        rowSelection={canSelectRows ? rowSelection : undefined}
        setRowSelection={canSelectRows ? setRowSelection : undefined}
        translations={getTableTranslations()}
        ordering={order}
        onOrderChange={onOrderChange}
        rowSelectionActions={rowSelectionActions}
        rowSelectionActionPlacement='top'
        showRowMenuWithSelection
        actions={[
          {
            label: t`Copy license plate`,
            actionType: 'copy',
            onClick: (car) => void copyToClipboardWithToast(car.licensePlate.number, t`License plate`)
          },
          ...(canSwapCars
            ? [
                {
                  label: t`Swap car`,
                  actionType: 'swap' as const,
                  disabled: (car: CarFleetViewRecord) => !car.fleetSchedule,
                  onClick: (car: CarFleetViewRecord) => setSwapTarget(toSwapTarget(car))
                }
              ]
            : []),
          {
            label: t`Assign or update driver`,
            actionType: 'edit',
            disabled: () => !notificationCars || !canAssignDrivers,
            onClick: setDriverCar
          },
          {
            label: t`Mark as sold`,
            actionType: 'remove',
            onClick: (car) => setEndOwnershipAction({ car, reason: EndOwnershipReasonValues.SOLD })
          },
          {
            label: t`Mark as decommissioned`,
            actionType: 'remove',
            onClick: (car) => setEndOwnershipAction({ car, reason: EndOwnershipReasonValues.WRECKED_SCRAPPED })
          },
          {
            label: t`Remove (not mine)`,
            actionType: 'delete',
            onClick: (car) => setEndOwnershipAction({ car, reason: EndOwnershipReasonValues.NEVER_OWNED })
          }
        ]}
        wrapInCard
      />
      {driverCarGroup && (
        <CarContactSheet
          userGroupId={currentUserGroupId!}
          car={driverCarGroup}
          canEditDriver={canAssignDrivers}
          translations={commonTranslations.notificationGroupOverview}
          open
          onOpenChange={(open) => !open && setDriverCar(undefined)}
        />
      )}
      <SwapFleetCarDialog
        target={swapTarget}
        onClose={() => setSwapTarget(null)}
        onSwapped={() => setRowSelection({})}
      />
      <NoddiAsyncConfirmationDialog
        open={endOwnershipAction !== undefined}
        onOpenChange={(open) => !open && setEndOwnershipAction(undefined)}
        title={getEndOwnershipTitle(endOwnershipAction)}
        description={
          endOwnershipLicensePlate
            ? t`This ends ownership of ${endOwnershipLicensePlate} and removes it from this list.`
            : undefined
        }
        confirmText={getEndOwnershipConfirmText(endOwnershipAction)}
        confirmActionLoading={isEndCarOwnershipPending}
        confirmAction={async () => {
          if (!endOwnershipAction) {
            return;
          }
          await endCarOwnership({
            userGroupId: currentUserGroupId!,
            carIds: [endOwnershipAction.car.id],
            endReason: endOwnershipAction.reason
          });
        }}
      >
        {endCarOwnershipError && <ApiErrorMessageInternal error={endCarOwnershipError} />}
      </NoddiAsyncConfirmationDialog>
    </div>
  );
};

const toSwapTarget = (car: CarFleetViewRecord | undefined): SwapTarget | null =>
  car?.fleetSchedule ? { car, fleetSchedule: car.fleetSchedule } : null;
